import {
  FieldControl,
  FieldFooter,
  FieldLabel,
  FieldShell,
  getInputClassName,
} from "../../field-ui";
import {
  forwardRef,
  useId,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import type { FormFieldInputProps, FormFieldRef } from "../../types";
import {
  encodeFio,
  fioErrorsToList,
  formatFioLine,
  hasFioErrors,
  isFioEmpty,
  parseFio,
  parseFioLine,
  validateFioField,
} from "../../../../lib/forms/fio";
import { reportFieldValue } from "../../../../lib/forms/reportFieldValue";

interface FioInputProps extends FormFieldInputProps {
  label: string;
  labelMuted?: boolean;
  required: boolean;
  patronymicRequired: boolean;
}

const FioInput = forwardRef<FormFieldRef, FioInputProps>((props, ref) => {
  const shellRef = useRef<HTMLDivElement>(null);
  const inputId = useId();
  const [text, setText] = useState(() =>
    formatFioLine(parseFio(props.initialValue)),
  );
  const [errorMessages, setErrorMessages] = useState<string[]>([]);
  const [validationState, setValidationState] = useState(true);

  const parts = parseFioLine(text);

  const syncValue = (line: string) => {
    const nextParts = parseFioLine(line);
    const encoded = isFioEmpty(nextParts) ? "" : encodeFio(nextParts);
    reportFieldValue(props.fieldName, encoded, props.onValueChange);
  };

  const clear = () => {
    setText("");
    setValidationState(true);
    setErrorMessages([]);
    reportFieldValue(props.fieldName, "", props.onValueChange);
  };

  const validate = () => {
    const nextErrors = validateFioField(
      { required: props.required },
      parts,
      props.patronymicRequired,
    );
    const messages = fioErrorsToList(nextErrors);
    setErrorMessages(messages);
    const valid = !hasFioErrors(nextErrors);
    setValidationState(valid);
    return valid;
  };

  useImperativeHandle(ref, () => ({
    setValidationState,
    value: isFioEmpty(parts) ? "" : encodeFio(parts),
    scrollToElement: (options) => {
      shellRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
        ...options,
      });
    },
    fieldId: props.fieldId,
    fieldName: props.fieldName,
    validate,
    clear,
  }));

  const placeholder = props.patronymicRequired
    ? "Фамилия Имя Отчество"
    : "Фамилия Имя";

  return (
    <FieldShell ref={shellRef}>
      <FieldLabel htmlFor={inputId} muted={props.labelMuted}>
        {props.label ? (
          <>
            {props.label}
            {props.required ? (
              <span className="text-red-500" aria-hidden="true">
                {" "}
                *
              </span>
            ) : null}
          </>
        ) : null}
      </FieldLabel>

      <FieldControl>
        <input
          id={inputId}
          type="text"
          value={text}
          onChange={(e) => {
            const line = e.target.value;
            setText(line);
            setValidationState(true);
            setErrorMessages([]);
            syncValue(line);
          }}
          onBlur={validate}
          autoComplete="name"
          placeholder={placeholder}
          aria-invalid={!validationState}
          className={getInputClassName(validationState)}
        />
      </FieldControl>

      <FieldFooter>
        {errorMessages.length > 0 ? (
          <span className="text-red-600" role="alert">
            {errorMessages.join(". ")}
          </span>
        ) : null}
      </FieldFooter>
    </FieldShell>
  );
});

FioInput.displayName = "FioInput";

export { FioInput };
export type { FioInputProps };
